import React from "react";
import { Link } from "react-router-dom";
import Button from "../inputs/Button";
import Badge from "../shared/Badge";
import EmptyState from "../shared/EmptyState";
import ProgressBar from "../shared/ProgressBar";

const CadreDashboardView = ({ user, data, onNavigate, onRefresh }) => {
  const { statistics = {}, panels = [] } = data || {};

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header with Profile Button */}
      <div className="mb-8 bg-gradient-to-r from-custom-blue to-custom-light-blue rounded-lg shadow-lg p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div className="text-white mb-4 md:mb-0">
            <h1 className="text-3xl font-bold">Cadre Dashboard</h1>
            <p className="mt-2 opacity-90">Welcome back, {user?.name}</p>
            <p className="text-sm opacity-80 mt-1">
              Member ID: #{user?.id || "N/A"}
            </p>
          </div>

          {/* Profile Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/cadre-profile"
              className="bg-white text-custom-blue px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors font-semibold shadow-md flex items-center justify-center"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Update Profile
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Profile Summary Card */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6 border-l-4 border-custom-blue">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-custom-light-blue rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {user?.name?.charAt(0).toUpperCase() || "C"}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Profile Completion</p>
              <div className="flex items-center gap-2">
                <ProgressBar
                  value={75}
                  max={100}
                  showLabel={false}
                  variant="success"
                  className="w-32 h-2"
                />
                <span className="text-sm font-semibold text-gray-700">75%</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-6 mt-3 sm:mt-0">
            <div className="text-center">
              <p className="text-xs text-gray-500">Rating</p>
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 text-yellow-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="ml-1 text-sm font-semibold">4.5</span>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">Experience</p>
              <p className="text-sm font-semibold text-gray-700">10+ Years</p>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">Expertise</p>
              <p className="text-sm font-semibold text-gray-700">3 Areas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Panels</p>
              <p className="text-3xl font-bold text-gray-900">
                {statistics.totalPanels || 0}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Panels</p>
              <p className="text-3xl font-bold text-green-600">
                {statistics.activePanels || 0}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed Panels</p>
              <p className="text-3xl font-bold text-purple-600">
                {statistics.completedPanels || 0}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Invitations</p>
              <p className="text-3xl font-bold text-orange-600">
                {statistics.pendingInvitations || 0}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <svg
                className="w-6 h-6 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* My Panels */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">My Panels</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {panels.length > 0 ? (
            panels.map((panel) => (
              <div key={panel.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {panel.Panel?.title || panel.title}
                    </h3>
                    {/* Description removed */}
                    <div className="flex items-center mt-2 space-x-4">
                      <Badge
                        variant={
                          panel.role === "ADMIN"
                            ? "purple"
                            : panel.role === "MEMBER"
                            ? "primary"
                            : "default"
                        }
                        size="sm"
                      >
                        {panel.role}
                      </Badge>
                      <Badge
                        variant={
                          (panel.Panel?.status || panel.status) ===
                          "IN_PROGRESS"
                            ? "warning"
                            : (panel.Panel?.status || panel.status) ===
                              "COMPLETED"
                            ? "success"
                            : "default"
                        }
                        size="sm"
                      >
                        {(
                          panel.Panel?.status ||
                          panel.status ||
                          "PENDING"
                        ).replace("_", " ")}
                      </Badge>
                      {(panel.Panel?.deadline || panel.deadline) && (
                        <span className="text-sm text-gray-500">
                          Deadline:{" "}
                          {new Date(
                            panel.Panel?.deadline || panel.deadline
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link to={`/panel-details/${panel.panelId || panel.id}`}>
                    <Button
                      variant="primary"
                      size="md"
                      className="ml-4 bg-custom-blue hover:bg-custom-light-blue"
                    >
                      View Panel
                    </Button>
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8">
              <EmptyState
                title="No Panels Yet"
                message="You are not assigned to any panels yet. Check back later for new panel invitations."
                className="shadow-none"
              />
            </div>
          )}
        </div>
      </div>

      {/* COI Update Reminder */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Conflict of Interest Update Required
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Please update your conflict of interest disclosure for the
                current year.
              </p>
            </div>
            <div className="mt-4">
              <Link
                to="/cadre-profile"
                className="text-sm font-medium text-yellow-800 hover:text-yellow-900"
              >
                Update COI →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadreDashboardView;
